import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type {
  FulfillmentAdminClient,
  FulfillmentOrderLine,
  InventoryRow,
} from '@/infrastructure/FulfillmentAdminClient';
import { V1_RESERVED_LOCATION, V1_WAREHOUSE_LOCATION } from '@/pages/fulfillment/components/lineLocations';
import { ROUTES } from '@/routes/routes';

export interface UseExistingStockAsk {
  locationCode: string;
  reason: string;
}

interface UseExistingStockModalProps {
  client: FulfillmentAdminClient;
  line: FulfillmentOrderLine;
  busy: boolean;
  changed: boolean;
  refusal: string | undefined;
  onHide: () => void;
  onConfirm: (ask: UseExistingStockAsk) => void;
}

/**
 * What the dialog may reserve from. The inventory API reports the Unassigned bucket (`WH#00`) as `pickable: false,
 * available: 0` because no picker can walk to it, but it is real counted stock that the Facade reserves from (the
 * switch handler pins the reservation to whatever location the operator names), so its free quantity is read from
 * `onHand - reserved` instead (SSD-7755). `V1#RESERVED` and any other virtual row stay hidden: they are counters, not stock.
 */
export const isUnassignedStock = (row: InventoryRow): boolean => row.locationCode === V1_WAREHOUSE_LOCATION;

const isStockedShelf = (row: InventoryRow): boolean =>
  !isUnassignedStock(row) && row.locationCode !== V1_RESERVED_LOCATION && row.onHand > 0;

/** Units nothing else holds at this location: the server's `available` for a shelf, `onHand - reserved` for Unassigned. */
export const freeAt = (row: InventoryRow): number =>
  isUnassignedStock(row) ? row.onHand - row.reserved : row.available;

const describe = (row: InventoryRow): string =>
  isUnassignedStock(row)
    ? `Unassigned (${row.locationCode}) — ${freeAt(row)} free, shelf not recorded`
    : `${row.locationCode} — ${freeAt(row)} free`;

/** Location quantities are advisory; only the Facade transaction can reserve the whole line. */
export function UseExistingStockModal({
  client,
  line,
  busy,
  changed,
  refusal,
  onHide,
  onConfirm,
}: UseExistingStockModalProps) {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  /** The SKU's Unassigned row when it holds any units, listed last and explained even when nothing is free there. */
  const [unassigned, setUnassigned] = useState<InventoryRow | undefined>(undefined);
  const [hasStockedShelf, setHasStockedShelf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [locationCode, setLocationCode] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setLoadError(false);
    const load = async () => {
      const inventory = new Map<string, InventoryRow>();
      let unassignedRow: InventoryRow | undefined;
      let foundStockedShelf = false;
      const seen = new Set<string>();
      let cursor: string | undefined;
      do {
        const page = await client.listInventory({ skus: [line.sku], limit: 500, ...(cursor ? { cursor } : {}) });
        if (ignore) {
          return;
        }
        for (const row of page.rows) {
          if (row.sku !== line.sku) {
            continue;
          }
          if (isUnassignedStock(row)) {
            unassignedRow = row.onHand > 0 ? row : undefined;
          } else {
            if (isStockedShelf(row)) {
              foundStockedShelf = true;
            }
            if (row.pickable !== false && row.available > 0) {
              inventory.set(row.locationCode, row);
            }
          }
        }
        cursor = page.nextCursor ?? undefined;
        if (cursor && seen.has(cursor)) {
          throw new Error('Repeated inventory cursor');
        }
        if (cursor) {
          seen.add(cursor);
        }
      } while (cursor);
      if (!ignore) {
        setRows([...inventory.values(), ...(unassignedRow && freeAt(unassignedRow) > 0 ? [unassignedRow] : [])]);
        setUnassigned(unassignedRow);
        setHasStockedShelf(foundStockedShelf);
      }
    };
    void load()
      .catch(() => {
        if (!ignore) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [client, line.sku, refresh]);

  const selected = rows.find((row) => row.locationCode === locationCode);
  const ready =
    !loading &&
    !loadError &&
    !changed &&
    selected &&
    freeAt(selected) >= line.totalQuantity &&
    reason.trim().length > 0;
  const onlyUnassigned = !loading && !loadError && unassigned !== undefined && !hasStockedShelf;
  const hide = () => {
    if (!busy) {
      onHide();
    }
  };
  return (
    <Modal show centered onHide={hide} aria-labelledby="use-existing-stock-title">
      <Modal.Header closeButton={!busy}>
        <Modal.Title id="use-existing-stock-title">
          Use existing stock — {line.sku} (line {line.lineNo})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Reserve all {line.totalQuantity} unit(s) from one location instead of awaiting a JIT purchase. No stock is
          moved and no purchase or delivery is cancelled.
        </p>
        <p className="small text-muted">
          Free quantities are per shelf, except Unassigned stock, which has no recorded shelf. Stock can change, and
          stock held by other orders across locations is checked again when you confirm.
        </p>
        {loading && <p role="status">Loading locations…</p>}
        {loadError && <Alert variant="danger">Could not load free stock. Refresh locations to try again.</Alert>}
        {!loading && !loadError && rows.length === 0 && !unassigned && (
          <Alert variant="info">No location has free stock for this SKU.</Alert>
        )}
        {onlyUnassigned && unassigned && (
          <Alert variant="info">
            This SKU&apos;s stock is Unassigned ({unassigned.locationCode}): {unassigned.onHand} on hand,{' '}
            {freeAt(unassigned)} free, shelf not recorded. Move it to a shelf on the{' '}
            <Link to={ROUTES.fulfillment.inventory} onClick={hide}>
              Inventory screen
            </Link>
            {freeAt(unassigned) >= line.totalQuantity
              ? ', or reserve from Unassigned below.'
              : `; only ${freeAt(unassigned)} of the ${line.totalQuantity} needed are free there.`}
          </Alert>
        )}
        <Form.Group className="mb-3" controlId="use-existing-stock-location">
          <Form.Label>Location</Form.Label>
          <Form.Select
            value={locationCode}
            disabled={busy || loading || loadError || changed}
            onChange={(event) => setLocationCode(event.target.value)}
          >
            <option value="">Choose a location</option>
            {rows.map((row) => (
              <option key={row.locationCode} value={row.locationCode} disabled={freeAt(row) < line.totalQuantity}>
                {describe(row)}
                {freeAt(row) < line.totalQuantity ? ` (need ${line.totalQuantity})` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Button
          variant="outline-secondary"
          size="sm"
          className="mb-3"
          disabled={busy || loading}
          onClick={() => setRefresh((value) => value + 1)}
        >
          Refresh locations
        </Button>
        <Form.Group controlId="use-existing-stock-reason">
          <Form.Label>Reason</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={500}
            value={reason}
            disabled={busy || changed}
            onChange={(event) => setReason(event.target.value)}
          />
          <Form.Text muted>Required; kept on the order history.</Form.Text>
        </Form.Group>
        {changed && (
          <Alert variant="warning" className="mt-3 mb-0">
            The line changed. Close this dialog and reopen it from the refreshed order.
          </Alert>
        )}
        {refusal && (
          <Alert variant="danger" className="mt-3 mb-0">
            {refusal}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={hide}>
          Keep awaiting purchase
        </Button>
        <Button disabled={busy || !ready} onClick={() => onConfirm({ locationCode, reason: reason.trim() })}>
          Reserve existing stock
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
